/**
 * WebGL2 point renderer — Track Y1 MVP + Track Z1 color modes + colormap LUT.
 * Pick/lenses still use CPU projectParticles via CompositeRenderer.
 *
 * Velocity path uses the same mapping as Canvas2D:
 *   t = clamp(‖v‖ · colorScale, 0, 1)
 * sampled from a 256×1 RGB LUT texture (shared ramps in colormap.ts).
 */

import type { SimView } from "../core/simView";
import type { RenderParams } from "../sim/params";
import { buildViewProjMatrix } from "./camera";
import {
  colormapLutRgb8,
  normalizeColormap,
  type ColormapId,
} from "./colormap";
import { resolveParticleColorPlan } from "./particleColor";
import type { Renderer } from "./types";

const VS = `#version 300 es
uniform mat4 u_mvp;
uniform float u_point;
in vec3 a_pos;
in float a_speed;
in vec3 a_color;
out float v_speed;
out vec3 v_color;
void main() {
  gl_Position = u_mvp * vec4(a_pos, 1.0);
  gl_PointSize = u_point;
  v_speed = a_speed;
  v_color = a_color;
}
`;

const FS = `#version 300 es
precision mediump float;
uniform float u_mode; // 0=uniform, 1=velocity, 2=per-vertex (cycle/type)
uniform float u_colorScale;
uniform vec3 u_uniform;
uniform sampler2D u_cmap;
in float v_speed;
in vec3 v_color;
out vec4 outColor;
void main() {
  vec2 c = gl_PointCoord - vec2(0.5);
  if (dot(c, c) > 0.25) discard;
  if (u_mode > 1.5) {
    outColor = vec4(v_color, 1.0);
  } else if (u_mode > 0.5) {
    // Match Canvas: t = clamp(speed * colorScale, 0, 1) — no extra 0.02 factor.
    float t = clamp(v_speed * u_colorScale, 0.0, 1.0);
    // Sample LUT center of texel strip (256 wide).
    vec3 rgb = texture(u_cmap, vec2(t, 0.5)).rgb;
    outColor = vec4(rgb, 1.0);
  } else {
    outColor = vec4(u_uniform, 1.0);
  }
}
`;

function compile(
  gl: WebGL2RenderingContext,
  type: number,
  src: string,
): WebGLShader {
  const sh = gl.createShader(type)!;
  gl.shaderSource(sh, src);
  gl.compileShader(sh);
  if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
    const log = gl.getShaderInfoLog(sh);
    gl.deleteShader(sh);
    throw new Error(`shader: ${log}`);
  }
  return sh;
}

export class WebGL2Renderer implements Renderer {
  readonly canvas: HTMLCanvasElement;
  lastDrawMs = 0;
  private gl: WebGL2RenderingContext;
  private prog: WebGLProgram;
  private vao: WebGLVertexArrayObject;
  private posBuf: WebGLBuffer;
  private speedBuf: WebGLBuffer;
  private colorBuf: WebGLBuffer;
  private cmapTex: WebGLTexture;
  private speeds = new Float32Array(0);
  private posF32 = new Float32Array(0);
  private colorF32 = new Float32Array(0);
  private cssW = 0;
  private cssH = 0;
  private ro: ResizeObserver | null = null;
  private uMvp: WebGLUniformLocation;
  private uPoint: WebGLUniformLocation;
  private uMode: WebGLUniformLocation;
  private uScale: WebGLUniformLocation;
  private uUniform: WebGLUniformLocation;
  private uCmap: WebGLUniformLocation;
  private loadedCmap: ColormapId | null = null;
  private cycleCache: {
    epoch: number;
    friends: Int32Array | null;
    maps: import("../analysis/cycleColor").CycleColorMaps | null;
  } = { epoch: -1, friends: null, maps: null };

  constructor(canvas: HTMLCanvasElement) {
    const gl = canvas.getContext("webgl2", {
      alpha: false,
      antialias: false,
      depth: true,
    });
    if (!gl) throw new Error("WebGL2 unavailable");
    this.canvas = canvas;
    this.gl = gl;

    const vs = compile(gl, gl.VERTEX_SHADER, VS);
    const fs = compile(gl, gl.FRAGMENT_SHADER, FS);
    const prog = gl.createProgram()!;
    gl.attachShader(prog, vs);
    gl.attachShader(prog, fs);
    gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
      throw new Error(`link: ${gl.getProgramInfoLog(prog)}`);
    }
    this.prog = prog;
    this.uMvp = gl.getUniformLocation(prog, "u_mvp")!;
    this.uPoint = gl.getUniformLocation(prog, "u_point")!;
    this.uMode = gl.getUniformLocation(prog, "u_mode")!;
    this.uScale = gl.getUniformLocation(prog, "u_colorScale")!;
    this.uUniform = gl.getUniformLocation(prog, "u_uniform")!;
    this.uCmap = gl.getUniformLocation(prog, "u_cmap")!;

    this.vao = gl.createVertexArray()!;
    this.posBuf = gl.createBuffer()!;
    this.speedBuf = gl.createBuffer()!;
    this.colorBuf = gl.createBuffer()!;
    gl.bindVertexArray(this.vao);
    const aPos = gl.getAttribLocation(prog, "a_pos");
    const aSp = gl.getAttribLocation(prog, "a_speed");
    const aCol = gl.getAttribLocation(prog, "a_color");
    gl.bindBuffer(gl.ARRAY_BUFFER, this.posBuf);
    gl.enableVertexAttribArray(aPos);
    gl.vertexAttribPointer(aPos, 3, gl.FLOAT, false, 0, 0);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.speedBuf);
    gl.enableVertexAttribArray(aSp);
    gl.vertexAttribPointer(aSp, 1, gl.FLOAT, false, 0, 0);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.colorBuf);
    gl.enableVertexAttribArray(aCol);
    gl.vertexAttribPointer(aCol, 3, gl.FLOAT, false, 0, 0);
    gl.bindVertexArray(null);

    // 256×1 RGB colormap LUT
    this.cmapTex = gl.createTexture()!;
    gl.bindTexture(gl.TEXTURE_2D, this.cmapTex);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    this.uploadColormap("thermal");

    gl.enable(gl.DEPTH_TEST);
    gl.depthFunc(gl.LESS);
  }

  private uploadColormap(id: ColormapId): void {
    if (this.loadedCmap === id) return;
    const gl = this.gl;
    const bytes = colormapLutRgb8(id);
    gl.bindTexture(gl.TEXTURE_2D, this.cmapTex);
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
    gl.texImage2D(
      gl.TEXTURE_2D,
      0,
      gl.RGB,
      256,
      1,
      0,
      gl.RGB,
      gl.UNSIGNED_BYTE,
      bytes,
    );
    this.loadedCmap = id;
  }

  observe(container: Element): void {
    this.ro?.disconnect();
    this.ro = new ResizeObserver(() => this.resize());
    this.ro.observe(container);
    this.resize();
  }

  disconnect(): void {
    this.ro?.disconnect();
    this.ro = null;
  }

  resize(): void {
    const parent = this.canvas.parentElement;
    const rect =
      parent?.getBoundingClientRect() ?? this.canvas.getBoundingClientRect();
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const w = Math.max(1, Math.floor(rect.width));
    const h = Math.max(1, Math.floor(rect.height));
    this.cssW = w;
    this.cssH = h;
    this.canvas.width = Math.floor(w * dpr);
    this.canvas.height = Math.floor(h * dpr);
    this.canvas.style.width = "100%";
    this.canvas.style.height = "100%";
    this.gl.viewport(0, 0, this.canvas.width, this.canvas.height);
  }

  clear(color: string): void {
    const m = /^#?([0-9a-f]{6})$/i.exec(color.trim());
    let r = 0,
      g = 0,
      b = 0;
    if (m) {
      const n = parseInt(m[1]!, 16);
      r = ((n >> 16) & 255) / 255;
      g = ((n >> 8) & 255) / 255;
      b = (n & 255) / 255;
    }
    const gl = this.gl;
    gl.clearColor(r, g, b, 1);
    gl.clearDepth(1);
    gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
  }

  draw(view: SimView, render: RenderParams): void {
    const t0 = performance.now();
    const gl = this.gl;
    const { n, dim, positions } = view;
    this.clear(render.background);

    if (this.posF32.length !== n * 3) this.posF32 = new Float32Array(n * 3);
    if (this.speeds.length !== n) this.speeds = new Float32Array(n);
    if (this.colorF32.length !== n * 3) this.colorF32 = new Float32Array(n * 3);

    for (let i = 0; i < n; i++) {
      const base = i * dim;
      this.posF32[i * 3] = positions[base]!;
      this.posF32[i * 3 + 1] = positions[base + 1]!;
      this.posF32[i * 3 + 2] = dim >= 3 ? positions[base + 2]! : 0;
    }

    const plan = resolveParticleColorPlan(view, render, this.cycleCache);
    let modeF = 0;
    if (plan.mode === "velocity") {
      modeF = 1;
      const tmp = new Float64Array(n);
      view.writeSpeeds(tmp);
      for (let i = 0; i < n; i++) this.speeds[i] = tmp[i]!;
      this.colorF32.fill(0.9);
      this.uploadColormap(normalizeColormap(render.colormap));
    } else if (plan.mode === "uniform") {
      modeF = 0;
      this.speeds.fill(0);
      for (let i = 0; i < n; i++) {
        this.colorF32[i * 3] = plan.rgb[0];
        this.colorF32[i * 3 + 1] = plan.rgb[1];
        this.colorF32[i * 3 + 2] = plan.rgb[2];
      }
    } else {
      modeF = 2;
      this.speeds.fill(0);
      this.colorF32.set(plan.rgb.subarray(0, n * 3));
    }

    const mvp = buildViewProjMatrix(render, this.cssW, this.cssH, dim);

    gl.useProgram(this.prog);
    gl.uniformMatrix4fv(this.uMvp, false, mvp);
    gl.uniform1f(
      this.uPoint,
      Math.max(1, render.pointSize * (window.devicePixelRatio || 1)),
    );
    gl.uniform1f(this.uMode, modeF);
    gl.uniform1f(this.uScale, render.colorScale);
    if (plan.mode === "uniform") {
      gl.uniform3f(this.uUniform, plan.rgb[0], plan.rgb[1], plan.rgb[2]);
    } else {
      gl.uniform3f(this.uUniform, 0.89, 0.91, 0.94);
    }

    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.cmapTex);
    gl.uniform1i(this.uCmap, 0);

    gl.enable(gl.DEPTH_TEST);
    gl.bindVertexArray(this.vao);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.posBuf);
    gl.bufferData(gl.ARRAY_BUFFER, this.posF32, gl.DYNAMIC_DRAW);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.speedBuf);
    gl.bufferData(gl.ARRAY_BUFFER, this.speeds, gl.DYNAMIC_DRAW);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.colorBuf);
    gl.bufferData(gl.ARRAY_BUFFER, this.colorF32, gl.DYNAMIC_DRAW);
    gl.drawArrays(gl.POINTS, 0, n);
    gl.bindVertexArray(null);

    this.lastDrawMs = performance.now() - t0;
  }
}

/** Try WebGL2; return null if unsupported or shader fail. */
export function tryCreateWebGL2Renderer(
  canvas: HTMLCanvasElement,
): WebGL2Renderer | null {
  try {
    return new WebGL2Renderer(canvas);
  } catch {
    return null;
  }
}
